import { FC, useContext, useEffect, useMemo, useRef } from "react";
import { SpecialtyTypeEnum } from "@app/projects/enumerators";
import { SpecialtiesSelector } from "@app/projects/network-assessment/multi-map-export/components/SpecialtiesSelector";
import { MultiMapExportAction } from "@app/projects/network-assessment/multi-map-export/state-management/multiMapExportStateManagement";
import { MultiMapExportContext } from "@app/projects/network-assessment/multi-map-export/MultiMapExportContext";
import { MultiMapExportSection } from "@app/projects/network-assessment/multi-map-export/components/MultiMapExportSection";
import { availableSpecialtyTypes, initialSpecialtySelection, orderSpecialtyTypes, reconcileSpecialtySelection, specialtiesInScope } from "@app/projects/network-assessment/multi-map-export/utils/specialtySelection";
import { useMultiMapExportSpecialties } from "@app/projects/network-assessment/multi-map-export/hooks/useMultiMapExportSpecialties";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import RoundCheckboxMultiSelect from "@app/shared/RoundCheckboxMultiSelect";

const SECTION_NUMBER = 3;

export const SpecialtySection: FC<React.PropsWithChildren<unknown>> = () => {
  const { launchContext, project, projectRunConfigurationId, selections, selectionsChanged } = useContext(MultiMapExportContext);
  const { isError, isLoading, isSuccess, specialties } = useMultiMapExportSpecialties(project?.projectId, Number(projectRunConfigurationId));
  const hasInitialized = useRef(false);

  const selectedTypes = selections?.selectedSpecialtyTypes ?? [];
  const selectedSpecialtyGroupCodes = selections?.selectedSpecialtyGroupCodes ?? [];
  const availableTypes = useMemo(() => availableSpecialtyTypes(specialties), [specialties]);
  const scopedSpecialties = useMemo(() => specialtiesInScope(specialties, selectedTypes), [specialties, selectedTypes]);

  useEffect(() => {
    // Once per mount: a refetch must not discard edits the user has already made.
    if (hasInitialized.current || !isSuccess) return;

    hasInitialized.current = true;
    selectionsChanged?.({
      type: MultiMapExportAction.SpecialtyTypesChanged,
      value: initialSpecialtySelection(specialties, launchContext),
    });
  }, [isSuccess, launchContext, selectionsChanged, specialties]);

  const handleTypesChange = (nextTypes: SpecialtyTypeEnum[]) => selectionsChanged?.({
    type: MultiMapExportAction.SpecialtyTypesChanged,
    value: {
      selectedSpecialtyGroupCodes: reconcileSpecialtySelection(specialties, selectedSpecialtyGroupCodes, selectedTypes, nextTypes),
      selectedSpecialtyTypes: nextTypes,
    },
  });

  const handleSpecialtiesChange = (codes: string[]) => selectionsChanged?.({
    type: MultiMapExportAction.SpecialtiesChanged,
    value: { selectedSpecialtyGroupCodes: codes },
  });

  const renderBody = () => {
    if (isError) {
      return (
        <p className="multi-map-export__section-error" data-testid="multi-map-export__specialty-error">
          We couldn&apos;t load the specialties for this project. Refresh the page to try again.
        </p>
      );
    }

    if (isLoading || !isSuccess) {
      return <LoadingSpinner />;
    }

    return (
      <>
        {availableTypes.length > 0 && (
          <div className="multi-map-export__field">
            <RoundCheckboxMultiSelect
              label="Specialty Type"
              onChange={(next) => handleTypesChange(orderSpecialtyTypes(next))}
              selectedValues={[...selectedTypes]}
              testId="multi-map-export__specialty-type"
              values={[...availableTypes]}
            />
            <p className="multi-map-export__field-helper-text">Selected types set which specialties are in scope.</p>
          </div>
        )}
        <SpecialtiesSelector
          hasSpecialties={specialties.length > 0}
          onChange={handleSpecialtiesChange}
          selectedSpecialtyGroupCodes={selectedSpecialtyGroupCodes}
          specialtiesInScope={scopedSpecialties}
        />
      </>
    );
  };

  return (
    <MultiMapExportSection
      sectionNumber={SECTION_NUMBER}
      subHeader="Select the specialties you would like to create maps for."
      testId="multi-map-export__specialty-section"
      title="Specialty"
    >
      {renderBody()}
    </MultiMapExportSection>
  );
};

export default SpecialtySection;
